import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest, ApiError } from "../../../shared/api/client";
import type { SessionUser } from "../../../shared/auth/access";
import { applyOperatorBrowserPolicy } from "../operator-browser-policy";
import { isTransientProductionFailure } from "../operator-connection";
import {
  bindProductionTerminalSession,
  productionGateway,
} from "../production-gateway";
import { classifyTerminalSessionRecovery } from "../terminal-session-recovery";
import type { TerminalBootstrap } from "../models";
import type { TerminalFailure } from "./terminal-view-model";

export type TerminalWorkspaceOptions = {
  user: SessionUser;
  notify: (message: string, tone?: "success" | "error") => void;
  onLogout: () => Promise<void>;
  onBack?: () => void;
};

function configuredTerminalCode(user: SessionUser) {
  const fromUrl = new URLSearchParams(window.location.search)
    .get("terminal")
    ?.trim();
  if (fromUrl) {
    window.localStorage.setItem("anl-production-terminal-code", fromUrl);
    return fromUrl;
  }
  return (
    window.localStorage.getItem("anl-production-terminal-code") ||
    (user.preview ? "TABLET-MEMET-1" : "")
  );
}

function terminalFailure(error: unknown): TerminalFailure {
  if (error instanceof ApiError) {
    return { code: error.code, message: error.message };
  }
  return {
    code: "TERMINAL_UNKNOWN_ERROR",
    message:
      error instanceof Error ? error.message : "Terminal doğrulanamadı.",
  };
}

export function useTerminalWorkspace(options: TerminalWorkspaceOptions) {
  useEffect(() => applyOperatorBrowserPolicy(document, window), []);
  const [terminalCode] = useState(() => configuredTerminalCode(options.user));
  const [context, setContext] = useState<TerminalBootstrap | null>(null);
  const bootstrapRequest = useRef(0);
  const bootstrapPollInFlight = useRef<number | null>(null);
  const shellMounted = useRef(true);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<TerminalFailure | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [temporaryTakeoverOpen, setTemporaryTakeoverOpen] = useState(false);
  const [assignmentRefreshPending, setAssignmentRefreshPending] =
    useState(false);
  const [assignmentRefreshMessage, setAssignmentRefreshMessage] = useState("");
  const [openingSetup, setOpeningSetup] = useState(false);
  const canOpenSetup = options.user.assignedRoles.includes("SISTEM_YONETICISI");

  const leaveTerminal = async () => {
    if (options.onBack) {
      options.onBack();
      return;
    }
    if (!canOpenSetup) {
      await options.onLogout();
      return;
    }
    if (openingSetup) return;
    setOpeningSetup(true);
    try {
      await apiRequest<void>("/api/session/active-role", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: "SISTEM_YONETICISI" }),
      });
      window.location.reload();
    } catch (error) {
      options.notify(
        error instanceof Error
          ? error.message
          : "Sistem yöneticisi rolü açılamadı.",
      );
      setOpeningSetup(false);
    }
  };
  const leaveTerminalLabel = options.onBack
    ? "Önceki ekrana dön"
    : canOpenSetup
      ? openingSetup
        ? "Kurulum açılıyor…"
        : "Sistem yöneticisiyle kur"
      : "Oturumu kapat";

  const refresh = useCallback(async () => {
    if (bootstrapPollInFlight.current !== null) return;
    if (!terminalCode) {
      setLoading(false);
      setFailure({
        code: "TERMINAL_CODE_MISSING",
        message: "Bu tablet henüz bir makine terminali olarak kaydedilmemiş.",
      });
      return;
    }
    const request = ++bootstrapRequest.current;
    bootstrapPollInFlight.current = request;
    try {
      const next = await productionGateway.bootstrapTerminal(terminalCode);
      if (!shellMounted.current || request !== bootstrapRequest.current) return;
      setContext(next);
      setFailure(null);
    } catch (nextError) {
      if (!shellMounted.current || request !== bootstrapRequest.current) return;
      const nextFailure = terminalFailure(nextError);
      if (!isTransientProductionFailure(nextFailure.code)) setContext(null);
      setFailure(nextFailure);
    } finally {
      if (bootstrapPollInFlight.current === request) {
        bootstrapPollInFlight.current = null;
      }
      if (shellMounted.current && request === bootstrapRequest.current) {
        setLoading(false);
      }
    }
  }, [terminalCode]);

  const recoverTerminalSession = useCallback(
    async (previous: {
      sessionId: string;
      operatorId: string;
      lineId: string;
    }) => {
      const request = ++bootstrapRequest.current;
      try {
        const next = await productionGateway.bootstrapTerminal(terminalCode);
        if (!shellMounted.current || request !== bootstrapRequest.current) {
          return "unavailable" as const;
        }
        bindProductionTerminalSession(
          terminalCode,
          next.session?.status === "ACTIVE" ? next.session.id : null,
        );
        setContext(next);
        setFailure(null);
        return classifyTerminalSessionRecovery(
          { ...previous, terminalCode },
          next,
        );
      } catch {
        return "unavailable" as const;
      }
    },
    [terminalCode],
  );

  const refreshAssignments = async () => {
    if (assignmentRefreshPending) return;
    const request = ++bootstrapRequest.current;
    setAssignmentRefreshPending(true);
    setAssignmentRefreshMessage("Güncel vardiya ataması kontrol ediliyor…");
    try {
      const nextContext = await productionGateway.bootstrapTerminal(terminalCode);
      if (!shellMounted.current || request !== bootstrapRequest.current) return;
      setContext(nextContext);
      setFailure(null);
      if (nextContext.assignments.length) {
        options.notify(
          "Vardiya ataması bulundu. Operatör giriş ekranı açılıyor.",
        );
      } else {
        setAssignmentRefreshMessage("Atamalar güncel.");
      }
    } catch (nextError) {
      if (!shellMounted.current || request !== bootstrapRequest.current) return;
      const nextFailure = terminalFailure(nextError);
      setFailure(nextFailure);
      setAssignmentRefreshMessage(
        "Atamalar kontrol edilemedi. Bağlantıyı kontrol edip yeniden deneyin.",
      );
      options.notify(nextFailure.message || "Atamalar kontrol edilemedi.");
    } finally {
      if (shellMounted.current) setAssignmentRefreshPending(false);
    }
  };

  useEffect(() => {
    shellMounted.current = true;
    void refresh();
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, 15_000);
    const refreshOnVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const refreshOnOnline = () => {
      setReconnecting(true);
      void refresh().finally(() => setReconnecting(false));
    };
    document.addEventListener("visibilitychange", refreshOnVisible);
    window.addEventListener("online", refreshOnOnline);
    return () => {
      shellMounted.current = false;
      bootstrapRequest.current += 1;
      bootstrapPollInFlight.current = null;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshOnVisible);
      window.removeEventListener("online", refreshOnOnline);
    };
  }, [refresh]);

  const machineLogout = async () => {
    try {
      await productionGateway.logoutTerminal(terminalCode);
      await refresh();
    } catch (error) {
      options.notify(
        error instanceof Error
          ? error.message
          : "Operatör çıkışı tamamlanamadı.",
      );
    }
  };

  return {
    terminalCode,
    context,
    loading,
    failure,
    reconnecting,
    temporaryTakeoverOpen,
    assignmentRefreshPending,
    assignmentRefreshMessage,
    openingSetup,
    canOpenSetup,
    leaveTerminalLabel,
    leaveTerminal,
    refresh,
    recoverTerminalSession,
    refreshAssignments,
    machineLogout,
    openTemporaryTakeover: () => {
      setAssignmentRefreshMessage("");
      setTemporaryTakeoverOpen(true);
    },
    closeTemporaryTakeover: () => setTemporaryTakeoverOpen(false),
  };
}
